import React, { createContext, useContext, useState, useEffect } from 'react';

const LowBandwidthContext = createContext();

export const useLowBandwidth = () => {
  const context = useContext(LowBandwidthContext);
  if (!context) {
    throw new Error('useLowBandwidth must be used within LowBandwidthProvider');
  }
  return context;
};

export const LowBandwidthProvider = ({ children }) => {
  const [isLowBandwidth, setIsLowBandwidth] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Load setting on mount
  useEffect(() => {
    const loadSetting = () => {
      try {
        const saved = localStorage.getItem('lowBandwidthMode');
        const enabled = saved === 'true';
        setIsLowBandwidth(enabled);
        
        // Apply CSS class to body
        if (enabled) {
          document.body.classList.add('low-bandwidth-mode');
        } else {
          document.body.classList.remove('low-bandwidth-mode');
        }
        
        console.log('[LOW BANDWIDTH] Mode loaded:', enabled);
      } catch (err) {
        console.error('[LOW BANDWIDTH] Failed to load setting:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadSetting();
  }, []);

  const toggleLowBandwidth = (enabled) => {
    try {
      setIsLowBandwidth(enabled);
      localStorage.setItem('lowBandwidthMode', enabled.toString());
      
      // Apply CSS class to body
      if (enabled) {
        document.body.classList.add('low-bandwidth-mode');
      } else {
        document.body.classList.remove('low-bandwidth-mode');
      }
      
      console.log('[LOW BANDWIDTH] Mode updated:', enabled);
      return true;
    } catch (err) {
      console.error('[LOW BANDWIDTH] Failed to save setting:', err);
      return false;
    }
  };

  // Get optimized image URL based on low bandwidth mode
  const getOptimizedImageUrl = (url, options = {}) => {
    if (!url) return url;
    if (!isLowBandwidth) return url;

    const { quality = 30, width = 400 } = options;

    // If it's a cloudinary URL, add optimization parameters
    if (url.includes('cloudinary.com')) {
      // Add quality and resize transformations
      const parts = url.split('/upload/');
      if (parts.length === 2) {
        return `${parts[0]}/upload/q_${quality},w_${width},f_auto/${parts[1]}`;
      }
    }

    // For other URLs, return as-is (could add proxy service here)
    return url;
  };

  // Get fetch options with low bandwidth header
  const getFetchOptions = (options = {}) => {
    if (!isLowBandwidth) return options;

    return {
      ...options,
      headers: {
        ...(options.headers || {}),
        'X-Low-Bandwidth': 'true',
        'X-Prefer-Quality': 'low',
      },
    };
  };

  const value = {
    isLowBandwidth,
    isLoading,
    toggleLowBandwidth,
    getOptimizedImageUrl,
    getFetchOptions,
  };

  return (
    <LowBandwidthContext.Provider value={value}>
      {children}
    </LowBandwidthContext.Provider>
  );
};
